/**
 * Classification Views editor (PMS views layer, migration 0094 — V3b).
 *
 * A View is a saved, named, ordered selection of lenses over the one facet pool
 * (decisions/2026-07-23_classification-and-model-composition.md §3). Exactly one view
 * is the firm-wide ACTIVE view — it governs the default lens set + order the securities
 * grid and export show. "System A" (GICS/MSCI) and "System B" (Bloomberg) are authored
 * here. An `includes_all_lenses` view shows every live dimension in taxonomy order.
 *
 * Discrete actions (create / activate / delete) commit on their own gesture (§3g
 * Pattern B — a modal Submit, or a one-click firm-wide toggle). The lens membership
 * editor stages its include+order choices and commits on Save (PUT /schemes/{id}/lenses).
 * All writes are classification.manage-gated + audited.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon, Badge, Button, Card, Group, Loader, Modal, Stack, Switch, Table, Text,
  TextInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCheck, IconGripVertical, IconLayoutList, IconPencil, IconPlus, IconTrash,
} from '@tabler/icons-react';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  classificationApi,
  type ClassificationDimension,
  type ClassificationScheme,
} from '../api/classification';

const SCHEMES_KEY = ['classification', 'schemes'] as const;
const errMsg = (e: unknown) => (e as ApiError)?.detail ?? (e instanceof Error ? e.message : 'Something went wrong.');

function reorder<T>(arr: T[], from: number, to: number): T[] {
  const c = arr.slice();
  const [m] = c.splice(from, 1);
  c.splice(to, 0, m);
  return c;
}

/** Create/edit a view's metadata (name, description, all-lenses toggle). */
function ViewMetaModal({
  opened, onClose, existing, onSubmit, busy,
}: {
  opened: boolean; onClose: () => void; existing: ClassificationScheme | null;
  onSubmit: (v: { name: string; description: string | null; includes_all_lenses: boolean }) => void;
  busy: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [allLenses, setAllLenses] = useState(false);
  // Re-seed the form whenever the modal opens for a different target.
  useEffect(() => {
    if (opened) {
      setName(existing?.name ?? '');
      setDescription(existing?.description ?? '');
      setAllLenses(existing?.includes_all_lenses ?? false);
    }
  }, [opened, existing]);

  return (
    <Modal opened={opened} onClose={onClose} centered
      title={<Text fw={600}>{existing ? 'Edit view' : 'New view'}</Text>}>
      <Stack>
        <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)}
          placeholder="e.g. System B (Bloomberg)" data-autofocus required />
        <TextInput label="Description" value={description} onChange={(e) => setDescription(e.currentTarget.value)}
          placeholder="optional" />
        <Switch label="Include all lenses (every dimension, in taxonomy order)"
          checked={allLenses} onChange={(e) => setAllLenses(e.currentTarget.checked)} />
        <Text size="xs" c="dimmed">
          With this off, choose and order specific lenses after saving. With it on, the view always shows every
          live dimension.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button loading={busy} disabled={!name.trim()}
            onClick={() => onSubmit({ name: name.trim(), description: description.trim() || null, includes_all_lenses: allLenses })}>
            {existing ? 'Save' : 'Create'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface LensRow { code: string; name: string; included: boolean }

/** Staged include+order editor for an explicit view's lens membership. */
function LensEditorModal({
  view, dims, onClose, onSaved,
}: {
  view: ClassificationScheme; dims: ClassificationDimension[]; onClose: () => void; onSaved: () => void;
}) {
  const initial = useMemo<LensRow[]>(() => {
    const byCode = new Map(dims.map((d) => [d.code, d.name]));
    const included = view.lenses
      .slice()
      .sort((a, b) => a.position - b.position)
      .filter((l) => byCode.has(l.dimension_code))
      .map((l) => ({ code: l.dimension_code, name: byCode.get(l.dimension_code)!, included: true }));
    const includedCodes = new Set(included.map((r) => r.code));
    const rest = dims
      .filter((d) => !includedCodes.has(d.code))
      .map((d) => ({ code: d.code, name: d.name, included: false }));
    return [...included, ...rest];
  }, [view, dims]);

  const [rows, setRows] = useState<LensRow[]>(initial);
  const [busy, setBusy] = useState(false);

  const onDragEnd = (res: DropResult) => {
    if (!res.destination) return;
    setRows((r) => reorder(r, res.source.index, res.destination!.index));
  };
  const toggle = (code: string) =>
    setRows((r) => r.map((x) => (x.code === code ? { ...x, included: !x.included } : x)));

  const save = async () => {
    setBusy(true);
    try {
      const codes = rows.filter((r) => r.included).map((r) => r.code);
      await classificationApi.setSchemeLenses(view.id, codes);
      notifications.show({ color: 'green', message: 'Lenses saved' });
      onSaved();
    } catch (e) {
      notifications.show({ color: 'red', autoClose: false, message: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const includedCount = rows.filter((r) => r.included).length;

  return (
    <Modal opened onClose={onClose} centered size="lg"
      title={<Text fw={600}>Lenses for "{view.name}"</Text>}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Check the lenses this view shows and drag to set their order. {includedCount} selected — this order is
          how columns appear in the securities grid and export when this view is active.
        </Text>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="lenses">
            {(dp) => (
              <Stack gap={2} ref={dp.innerRef} {...dp.droppableProps}>
                {rows.map((r, i) => (
                  <Draggable key={r.code} draggableId={r.code} index={i}>
                    {(dr) => (
                      <Group ref={dr.innerRef} {...dr.draggableProps} gap="sm" wrap="nowrap"
                        style={{ ...dr.draggableProps.style, padding: '4px 6px', borderRadius: 6,
                          background: r.included ? 'var(--mantine-color-azul-0)' : 'transparent' }}>
                        <span {...dr.dragHandleProps} style={{ cursor: 'grab', display: 'flex' }}>
                          <IconGripVertical size={16} color="var(--mantine-color-gray-5)" />
                        </span>
                        <Switch size="xs" checked={r.included} onChange={() => toggle(r.code)} />
                        <Text size="sm" fw={r.included ? 600 : 400} c={r.included ? undefined : 'dimmed'}>{r.name}</Text>
                        <Text size="xs" c="dimmed" ff="monospace">{r.code}</Text>
                      </Group>
                    )}
                  </Draggable>
                ))}
                {dp.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button loading={busy} onClick={save}>Save lenses</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function ClassificationViewsPanel() {
  const qc = useQueryClient();
  const schemesQ = useQuery({ queryKey: SCHEMES_KEY, queryFn: classificationApi.schemes, staleTime: 30_000 });
  const dimsQ = useQuery({
    queryKey: ['classification', 'dimensions'], queryFn: classificationApi.dimensions, staleTime: 60_000,
  });

  const [metaOpen, setMetaOpen] = useState(false);
  const [editing, setEditing] = useState<ClassificationScheme | null>(null);   // null = create
  const [lensView, setLensView] = useState<ClassificationScheme | null>(null);
  const [confirmDel, setConfirmDel] = useState<ClassificationScheme | null>(null);

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: SCHEMES_KEY });
    // consumers of the active view (securities grid/export) must re-read
    await qc.invalidateQueries({ queryKey: ['classification', 'active-view'] });
  };

  const meta = useMutation({
    mutationFn: (v: { name: string; description: string | null; includes_all_lenses: boolean }) =>
      editing
        ? classificationApi.updateScheme(editing.id, v)
        : classificationApi.createScheme(v),
    onSuccess: async () => {
      await invalidate();
      notifications.show({ color: 'green', message: editing ? 'View saved' : 'View created' });
      setMetaOpen(false);
    },
    onError: (e) => notifications.show({ color: 'red', autoClose: false, message: errMsg(e) }),
  });
  const activate = useMutation({
    mutationFn: (id: string) => classificationApi.activateScheme(id),
    onSuccess: async () => { await invalidate(); notifications.show({ color: 'green', message: 'Active view set' }); },
    onError: (e) => notifications.show({ color: 'red', autoClose: false, message: errMsg(e) }),
  });
  const del = useMutation({
    mutationFn: (id: string) => classificationApi.deleteScheme(id),
    onSuccess: async () => { await invalidate(); setConfirmDel(null); notifications.show({ color: 'green', message: 'View deleted' }); },
    onError: (e) => notifications.show({ color: 'red', autoClose: false, message: errMsg(e) }),
  });

  const dims = dimsQ.data ?? [];
  const dimName = useMemo(() => new Map(dims.map((d) => [d.code, d.name])), [dims]);
  const schemes = schemesQ.data ?? [];

  const lensPreview = (s: ClassificationScheme) => {
    if (s.includes_all_lenses) return 'All lenses';
    if (!s.lenses.length) return 'No lenses yet';
    return s.lenses.slice().sort((a, b) => a.position - b.position)
      .map((l) => dimName.get(l.dimension_code) ?? l.dimension_code).join(' › ');
  };

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs"><IconLayoutList size={20} /><Text fw={600}>Views</Text></Group>
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
            onClick={() => { setEditing(null); setMetaOpen(true); }}>New view</Button>
        </Group>
        <Text size="sm" c="dimmed">
          A view is a saved, ordered selection of lenses. The <b>active</b> view sets the default lens columns
          (and their order) shown across the securities master. Author "System A" / "System B" here.
        </Text>

        {schemesQ.isLoading && <Loader size="sm" />}
        {schemesQ.isError && <Text c="red" size="sm">{errMsg(schemesQ.error)}</Text>}

        {schemes.length > 0 && (
          <Table verticalSpacing="xs">
            <Table.Thead><Table.Tr>
              <Table.Th>View</Table.Th>
              <Table.Th>Lenses</Table.Th>
              <Table.Th w={280} />
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {schemes.map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={600} size="sm">{s.name}</Text>
                      {s.is_active && <Badge color="verde" variant="light" leftSection={<IconCheck size={11} />}>Active</Badge>}
                      <Badge color={s.includes_all_lenses ? 'azul' : 'gray'} variant="light">
                        {s.includes_all_lenses ? 'All lenses' : `${s.lenses.length} lens${s.lenses.length === 1 ? '' : 'es'}`}
                      </Badge>
                    </Group>
                    {s.description && <Text size="xs" c="dimmed">{s.description}</Text>}
                    <Text size="xs" c="dimmed">{lensPreview(s)}</Text>
                  </Table.Td>
                  <Table.Td />
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={s.is_active ? 'Already the active view' : 'Make this the firm-wide active view'} withArrow>
                        <Button size="xs" variant={s.is_active ? 'default' : 'light'} disabled={s.is_active || activate.isPending}
                          onClick={() => activate.mutate(s.id)}>Set active</Button>
                      </Tooltip>
                      <Tooltip label={s.includes_all_lenses ? 'Turn off "all lenses" first to choose specific lenses' : 'Choose & order lenses'} withArrow>
                        <span>
                          <ActionIcon variant="subtle" aria-label="Edit lenses" disabled={s.includes_all_lenses}
                            onClick={() => setLensView(s)}><IconLayoutList size={16} /></ActionIcon>
                        </span>
                      </Tooltip>
                      <Tooltip label="Rename / settings" withArrow>
                        <ActionIcon variant="subtle" aria-label="Edit view"
                          onClick={() => { setEditing(s); setMetaOpen(true); }}><IconPencil size={16} /></ActionIcon>
                      </Tooltip>
                      <Tooltip label={s.is_active ? 'Activate another view before deleting this one' : 'Delete view'} withArrow>
                        <span>
                          <ActionIcon variant="subtle" color="red" aria-label="Delete view" disabled={s.is_active}
                            onClick={() => setConfirmDel(s)}><IconTrash size={16} /></ActionIcon>
                        </span>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <ViewMetaModal opened={metaOpen} onClose={() => setMetaOpen(false)} existing={editing}
        busy={meta.isPending} onSubmit={(v) => meta.mutate(v)} />

      {lensView && (
        <LensEditorModal view={lensView} dims={dims}
          onClose={() => setLensView(null)}
          onSaved={async () => { await invalidate(); setLensView(null); }} />
      )}

      <Modal opened={!!confirmDel} onClose={() => setConfirmDel(null)} centered
        title={<Text fw={600}>Delete view</Text>}>
        <Stack>
          <Text size="sm">Delete the view <b>{confirmDel?.name}</b>? This does not affect any security's
            classifications — only this saved lens selection.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button color="red" loading={del.isPending} onClick={() => confirmDel && del.mutate(confirmDel.id)}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
